import { Box, Flex } from '@amzn/ring-ui-react-components';
import { ChangeEvent } from 'react';
import { Card, CardBody } from 'src/shared/components/Card';
import { Checkbox } from 'src/shared/components/Form';
import { createDateTimeFromISO, formatTime } from 'src/shared/utils';
import { useHistoryName } from './useHistoryName';
import { Video } from '../MediaItemList';
import { Tooltip } from '../Tooltip';

export interface MediaItemProps {
  idx: number;
  isPreviewing: boolean;
  isChecked: boolean;
  item: Video;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
  select: (videoId: string) => () => void;
}

export const MediaItem = ({
  disabled,
  idx,
  isChecked,
  isPreviewing,
  onChange,
  item,
  select,
}: MediaItemProps) => {
  const dt = createDateTimeFromISO(item.startTime);
  const date = dt.toFormat('EEE, MMM dd');
  const time = formatTime(dt);
  const eventName = useHistoryName(item);
  const isDisabled = disabled || !Boolean(item.url);

  return (
    <Card
      onClick={select(item?.id)}
      aria-selected={isPreviewing}
      hoverable={true}
      borderRadius={0}
      cursor="pointer"
      borderStyle="none"
      borderBottomColor="tertiary.500"
      borderBottomStyle="solid"
      borderBottomWidth={2}
      _hover={{
        bg: 'primary.100',
        mt: -1 / 2,
        mx: -1 / 2,
        mb: 0,
        borderRadius: 'small',
        borderColor: 'primary.500',
        borderStyle: 'solid',
        borderWidth: 2,
      }}
      _selected={{
        bg: 'primary.100',
        mt: -1 / 2,
        mx: -1 / 2,
        mb: 0,
        borderRadius: 'small',
        borderColor: 'primary.500',
        borderStyle: 'solid',
        borderWidth: 2,
      }}
      data-testid="media-item"
    >
      <CardBody pt={3} px={4} data-testid="media-item-body">
        <Flex flexDirection="row">
          <Box mr={4} position="relative">
            <Tooltip id={`tooltip-disabled-${idx}`} place="top">
              {disabled
                ? 'Neighbors only supports a maximum of five images or videos per feed post.'
                : 'The video for this event does not exist.'}
            </Tooltip>
            <Checkbox
              size="lg"
              onChange={onChange}
              checked={isChecked}
              disabled={isDisabled}
              data-tip={true}
              data-tip-disable={!isDisabled}
              data-for={`tooltip-disabled-${idx}`}
            />
          </Box>
          <Flex flexDirection="row" justifyContent="space-between" flex={1}>
            <Flex flexDirection="column">
              <Box
                color="neutral.300"
                textStyle="body-small-regular"
                mb={1 / 2}
              >
                {item.deviceName}
              </Box>
              <Box
                color="neutral.500"
                textStyle="body-normal-medium"
                textTransform="capitalize"
              >
                {eventName}
              </Box>
            </Flex>
            <Flex flexDirection="column">
              <Box
                color="neutral.300"
                textStyle="body-small-regular"
                mb={1 / 2}
              >
                {date}
              </Box>
              <Box color="neutral.300" textStyle="body-small-regular">
                {time}
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
};
